import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';
import { createDmDesignCategorySchema } from '@pms/shared';
import type { CreateDmDesignCategoryInput, AddDmDesignCategorySizeInput } from '@pms/shared';
import { dmTeamService } from '@/services/dm-team.service';
import type { DmDesignCategory } from '@/services/dm-team.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function DesignCategoriesModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [sizeInput, setSizeInput] = useState('');
  const [pendingSizes, setPendingSizes] = useState<string[]>([]);
  const [addSizeCatId, setAddSizeCatId] = useState<string | null>(null);
  const [newSizeName, setNewSizeName] = useState('');
  const [editCat, setEditCat] = useState<DmDesignCategory | null>(null);
  const [editName, setEditName] = useState('');
  const [editSize, setEditSize] = useState<{ categoryId: string; sizeId: string; name: string } | null>(null);
  const [editSizeName, setEditSizeName] = useState('');

  const { data } = useQuery({
    queryKey: ['dm-team', 'design-categories'],
    queryFn: () => dmTeamService.listDesignCategories(false),
    enabled: open,
  });

  const form = useForm<CreateDmDesignCategoryInput>({
    resolver: zodResolver(createDmDesignCategorySchema),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateDmDesignCategoryInput) => dmTeamService.createDesignCategory(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] });
      setCreateOpen(false);
      form.reset();
      setPendingSizes([]);
      toast.success('Category created');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create'),
  });

  const addSizeMutation = useMutation({
    mutationFn: ({ categoryId, body }: { categoryId: string; body: AddDmDesignCategorySizeInput }) =>
      dmTeamService.addDesignCategorySize(categoryId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] });
      setAddSizeCatId(null);
      setNewSizeName('');
      toast.success('Size added');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to add size'),
  });

  const deleteSizeMutation = useMutation({
    mutationFn: ({ categoryId, sizeId }: { categoryId: string; sizeId: string }) =>
      dmTeamService.deleteDesignCategorySize(categoryId, sizeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] });
      toast.success('Size removed');
    },
    onError: () => toast.error('Failed to remove size'),
  });

  const updateSizeMutation = useMutation({
    mutationFn: ({ categoryId, sizeId, name }: { categoryId: string; sizeId: string; name: string }) =>
      dmTeamService.updateDesignCategorySize(categoryId, sizeId, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] });
      setEditSize(null);
      toast.success('Size renamed');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to rename size'),
  });

  const updateCatMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      dmTeamService.updateDesignCategory(id, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'design-categories'] });
      setEditCat(null);
      toast.success('Category renamed');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to rename'),
  });

  const categories = data?.data ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Design Categories</DialogTitle>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> New Category
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            {categories.length === 0 && (
              <p className="text-sm text-muted-foreground p-6 text-center border rounded-lg">
                No categories yet
              </p>
            )}
            {categories.map((cat: DmDesignCategory) => (
              <div key={cat.id} className="rounded-lg border bg-card p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{cat.name}</span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 sm:h-8 sm:w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => { setEditCat(cat); setEditName(cat.name); }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setAddSizeCatId(cat.id)}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Size
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cat.sizes.map((s) => (
                    <Badge key={s.id} variant="secondary" className="text-xs flex items-center gap-1 pr-1">
                      {s.name}
                      <button
                        onClick={() => { setEditSize({ categoryId: cat.id, sizeId: s.id, name: s.name }); setEditSizeName(s.name); }}
                        className="ml-1 hover:text-teal-600"
                        title="Rename"
                      >
                        <Pencil className="w-2.5 h-2.5" />
                      </button>
                      <button
                        onClick={() => deleteSizeMutation.mutate({ categoryId: cat.id, sizeId: s.id })}
                        className="hover:text-destructive"
                        title="Remove"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                  {cat.sizes.length === 0 && (
                    <span className="text-xs text-muted-foreground">No sizes yet</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Category dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(v) => {
          if (!v) { setPendingSizes([]); setSizeInput(''); form.reset(); }
          setCreateOpen(v);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Design Category</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit((d) =>
              createMutation.mutate({
                ...d,
                sizes: pendingSizes.map((n, i) => ({ name: n, sort_order: i })),
              }),
            )}
            className="space-y-4"
          >
            <div>
              <Label>Category Name *</Label>
              <Input {...form.register('name')} className="mt-1" />
            </div>
            <div>
              <Label>Sizes</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={sizeInput}
                  onChange={(e) => setSizeInput(e.target.value)}
                  placeholder="e.g. Story-9:16"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (sizeInput.trim()) {
                        setPendingSizes((p) => [...p, sizeInput.trim()]);
                        setSizeInput('');
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (sizeInput.trim()) {
                      setPendingSizes((p) => [...p, sizeInput.trim()]);
                      setSizeInput('');
                    }
                  }}
                >
                  +
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pendingSizes.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">
                    {s}{' '}
                    <button
                      type="button"
                      onClick={() => setPendingSizes((p) => p.filter((_, j) => j !== i))}
                      className="ml-1 hover:text-destructive"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Category dialog */}
      <Dialog open={!!editCat} onOpenChange={(v) => !v && setEditCat(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-1"
                placeholder="Category name"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCat(null)}>Cancel</Button>
              <Button
                disabled={!editName.trim() || updateCatMutation.isPending}
                onClick={() => editCat && updateCatMutation.mutate({ id: editCat.id, name: editName.trim() })}
              >
                Save
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Size dialog */}
      <Dialog open={!!editSize} onOpenChange={(v) => !v && setEditSize(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename Size</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Size Name *</Label>
              <Input
                value={editSizeName}
                onChange={(e) => setEditSizeName(e.target.value)}
                className="mt-1"
                placeholder="e.g. Post-1080x1080px"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditSize(null)}>Cancel</Button>
              <Button
                disabled={!editSizeName.trim() || updateSizeMutation.isPending}
                onClick={() => editSize && updateSizeMutation.mutate({ ...editSize, name: editSizeName.trim() })}
              >
                Save
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Size dialog */}
      <Dialog open={!!addSizeCatId} onOpenChange={(v) => !v && setAddSizeCatId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Size</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Size Name *</Label>
              <Input
                value={newSizeName}
                onChange={(e) => setNewSizeName(e.target.value)}
                className="mt-1"
                placeholder="e.g. Post-1080x1080px"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddSizeCatId(null)}>
                Cancel
              </Button>
              <Button
                disabled={!newSizeName.trim() || addSizeMutation.isPending}
                onClick={() =>
                  addSizeCatId &&
                  addSizeMutation.mutate({
                    categoryId: addSizeCatId,
                    body: { name: newSizeName.trim(), sort_order: 0 },
                  })
                }
              >
                Add
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
